import { Colors } from '@kvika/audur-theme'
import { useEffect, useState } from 'react'

import { GridItem, MainGridContainer } from '../../components/grid-container'
import { CalculatorPrimary } from '../../prismic/prismicTypes'
import { TranslatedStrings } from '../../utils/translations/TranslatedStrings'
import { ColumnClass, Gutters } from '../../utils/types/Types'
import { formatNumberISFloat } from '../../utils/Utils'

import ShowMoreButton from './components/calculatorSettingsButton'
import MobileToggle from './components/mobileToggle'
import NewCalculatorChart from './containers/newCalculatorChart'
import NewCalculatorFinancialPlan from './containers/newCalculatorFinancialPlan'
import NewCalculatorHeader from './containers/newCalculatorHeader'
import NewCalculatorPaymentPlan from './containers/newCalculatorPaymentPlan'
import NewCalculatorSummary from './containers/newCalculatorSummary'
import { LocalCalculationValues, useCalculatorLogic } from './hooks/useCalculatorLogic'
import { initialLoanCalculationCalculatedData, LoanCalculationInputs, LoanTypeEnum } from './newCalculator.models'
import styles from './newCalculator.module.scss'

type Props = {
	prismicData: CalculatorPrimary
	onLoanValuesChange: (loanValues: LocalCalculationValues) => void
}

const NewCalculatorSlice = ({ prismicData, onLoanValuesChange }: Props) => {
	const [isPaymentReductionPlanActiveMobile, setIsPaymentReductionPlanActiveMobile] = useState(false)
	const [displayedPaymentPlanType, setDisplayedPaymentPlanType] = useState<LoanTypeEnum>()

	const {
		loanCalculationValues,
		calculatedData,
		activeCalculatedData,
		expandedPaymentPlan,
		publicShowSettings,
		changePublicShowSettings,
		onInputValueChange,
		onMultipleInputValueChange,
		toggleExpandPaymentPlan,
		defaultOptions,
	} = useCalculatorLogic()

	useEffect(() => {
		onLoanValuesChange(loanCalculationValues)
	}, [loanCalculationValues, onLoanValuesChange])

	useEffect(() => {
		if (!expandedPaymentPlan) return

		setDisplayedPaymentPlanType(expandedPaymentPlan)
	}, [expandedPaymentPlan])

	const handleExpandPaymentPlan = (type: LoanTypeEnum) => {
		const scrollIntoView = expandedPaymentPlan !== type
		toggleExpandPaymentPlan(type)
		const el = document.getElementById('payment-plan')
		if (el && scrollIntoView) {
			el.scrollIntoView({ behavior: 'smooth' })
			setTimeout(() => {
				window.scrollBy(0, -30)
			}, 400)
		}
	}

	return (
		<MainGridContainer backgroundColor={Colors.DarkGray} className={styles.container} gutters={Gutters.None}>
			<GridItem className={ColumnClass.All}>
				<div id='loan_calculator' data-testid='mortgage-calculator'>
					<div className={styles.wrap}>
						<NewCalculatorHeader
							loanAmount={loanCalculationValues.ASSET_FORMATION.loanAmount}
							proprtyMarketValue={loanCalculationValues.ASSET_FORMATION.propertyMarketValueAmount}
							onInputValueChange={(value: number | boolean, propertyName: keyof LoanCalculationInputs) =>
								onInputValueChange(value, propertyName, true, true)
							}
							calculatorPrismicTooltipData={prismicData}
							loanTerm={loanCalculationValues.ASSET_FORMATION.numberOfPaymentsMonths}
							onMultipleInputValueChange={(firstChange, secondChange) => {
								onMultipleInputValueChange(firstChange, secondChange, true, true)
							}}
							defaultOptions={defaultOptions}
						/>

						<div
							className={`${styles.body} ${
								!isPaymentReductionPlanActiveMobile ? styles.activeSummaryLeft : styles.activeSummaryRight
							}`}
						>
							<MobileToggle
								isPaymentReductionPlanActiveMobile={isPaymentReductionPlanActiveMobile}
								setIsPaymentReductionPlanActiveMobile={setIsPaymentReductionPlanActiveMobile}
								toggleExpandPaymentPlan={(type: LoanTypeEnum) => expandedPaymentPlan && toggleExpandPaymentPlan(type)}
							/>

							{/* Asset Formation Summary */}
							<div
								className={`${styles.bodyTopLeft} ${!isPaymentReductionPlanActiveMobile ? styles.activeSummary : ''}`}
								data-testid='calculator-summary-asset-formation'
							>
								<NewCalculatorSummary
									title={TranslatedStrings.is.newCalculator.equalization}
									svg='/svg/graph_upward.svg'
									desciption={
										formatNumberISFloat(
											calculatedData?.ASSET_FORMATION.interestRate ??
												(defaultOptions?.interestRates.find((x) => x.productType === 'AssetFormation')?.interestRate ??
													0.089) * 100
										) + TranslatedStrings.is.newCalculator.equalizationDescription
									}
									interestRate={formatNumberISFloat(
										calculatedData?.ASSET_FORMATION.interestRate ??
											(defaultOptions?.interestRates.find((x) => x.productType === 'AssetFormation')?.interestRate ??
												0.089) * 100
									)}
									payedMonthly={calculatedData?.ASSET_FORMATION.installments[1].total ?? 0}
									estimatedEquityAfterXYears={calculatedData?.ASSET_FORMATION.estimatedEquityAfterXYears ?? 0}
									isPaymentReductionPlan={false}
									calculatorPrismicTooltipData={prismicData}
									objectName='EqualPrincipal'
									showOnMobile={!isPaymentReductionPlanActiveMobile}
									durationOfReduction={loanCalculationValues.PAYMENT_REDUCTION.optionBLengthInYears}
									propertyEquity={
										loanCalculationValues.ASSET_FORMATION.propertyMarketValueAmount -
										loanCalculationValues.ASSET_FORMATION.loanAmount
									}
								/>
							</div>

							{/* Payment Reduction Summary */}
							<div
								className={`${styles.bodyTopRight} ${isPaymentReductionPlanActiveMobile ? styles.activeSummary : ''}`}
								data-testid='calculator-summary-payment-reduction'
							>
								<NewCalculatorSummary
									title={TranslatedStrings.is.newCalculator.reduction}
									svg='/svg/calculator_interest.svg'
									desciption={
										formatNumberISFloat(
											calculatedData?.PAYMENT_REDUCTION.interestRate ??
												(defaultOptions?.interestRates.find((x) => x.productType === 'PaymentReduction')
													?.interestRate ?? 0.089) * 100
										) + TranslatedStrings.is.newCalculator.reductionDescription
									}
									interestRate={formatNumberISFloat(
										calculatedData?.PAYMENT_REDUCTION.interestRate ??
											(defaultOptions?.interestRates.find((x) => x.productType === 'PaymentReduction')?.interestRate ??
												0.089) * 100
									)}
									payedMonthly={calculatedData?.PAYMENT_REDUCTION.avgPayment ?? 0}
									estimatedEquityAfterXYears={calculatedData?.PAYMENT_REDUCTION.estimatedEquityAfterXYears ?? 0}
									reductionPaymentAfterXYears={calculatedData?.PAYMENT_REDUCTION.reductionPaymentAfterXYears ?? 0}
									isPaymentReductionPlan
									durationOfReduction={loanCalculationValues.PAYMENT_REDUCTION.optionBLengthInYears}
									calculatorPrismicTooltipData={prismicData}
									objectName='EqualPayments'
									showOnMobile={isPaymentReductionPlanActiveMobile}
									propertyEquity={
										loanCalculationValues.PAYMENT_REDUCTION.propertyMarketValueAmount -
										loanCalculationValues.PAYMENT_REDUCTION.loanAmount
									}
								/>
							</div>

							{/* Chart */}
							<div className={styles.bodyChart}>
								<NewCalculatorChart
									dataEqualization={calculatedData?.ASSET_FORMATION?.graphData ?? []}
									dataReduction={calculatedData?.PAYMENT_REDUCTION?.graphData ?? []}
								/>
							</div>

							{/* Asset Formation Financial Plan */}
							<div
								className={`${styles.bodyBottomLeft} ${isPaymentReductionPlanActiveMobile ? styles.mobileHidden : ''}`}
								aria-expanded={publicShowSettings}
							>
								<NewCalculatorFinancialPlan
									{...loanCalculationValues.ASSET_FORMATION}
									fixedInterestPercentage={
										loanCalculationValues.ASSET_FORMATION.useCustomInterestRate
											? loanCalculationValues.ASSET_FORMATION.fixedInterestPercentage
											: calculatedData?.ASSET_FORMATION.interestRate
									}
									objectName={LoanTypeEnum.ASSET_FORMATION}
									onInputValueChange={(value: number | boolean | string, propertyName: keyof LoanCalculationInputs) =>
										onInputValueChange(value, propertyName, true, false)
									}
									totalPaid={calculatedData?.ASSET_FORMATION.totalRepayment ?? 0}
									loanTerm={loanCalculationValues.ASSET_FORMATION.numberOfPaymentsMonths}
									ahk={calculatedData?.ASSET_FORMATION.annualPercentageRate ?? 0}
									calculatorPrismicTooltipData={prismicData}
									isExpanded={expandedPaymentPlan === LoanTypeEnum.ASSET_FORMATION}
									onExpand={() => handleExpandPaymentPlan(LoanTypeEnum.ASSET_FORMATION)}
									showOnMobile={!isPaymentReductionPlanActiveMobile}
									estimatedPropertyAnnual10YearOption={
										loanCalculationValues.ASSET_FORMATION.estimatedPropertyAnnual10YearOption
									}
									estimatedPropertyAnnualIncrease={
										loanCalculationValues.ASSET_FORMATION.annualHousingPriceIncreasePercentage
									}
									isPaymentReductionPlan={false}
									settlementCosts={calculatedData?.ASSET_FORMATION.settlementCosts ?? []}
									loanAmount={loanCalculationValues.ASSET_FORMATION.loanAmount}
									defaultOptions={defaultOptions}
								/>
							</div>

							{/* Payment Reduction Financial Plan */}
							<div
								className={`${styles.bodyBottomRight} ${
									!isPaymentReductionPlanActiveMobile ? styles.mobileHidden : ''
								}`}
								aria-expanded={publicShowSettings}
							>
								<NewCalculatorFinancialPlan
									{...loanCalculationValues.PAYMENT_REDUCTION}
									fixedInterestPercentage={
										loanCalculationValues.PAYMENT_REDUCTION.useCustomInterestRate
											? loanCalculationValues.PAYMENT_REDUCTION.fixedInterestPercentage
											: calculatedData?.PAYMENT_REDUCTION.interestRate
									}
									onInputValueChange={(value: number | boolean | string, propertyName: keyof LoanCalculationInputs) =>
										onInputValueChange(value, propertyName, false, true)
									}
									objectName={LoanTypeEnum.PAYMENT_REDUCTION}
									totalPaid={calculatedData?.PAYMENT_REDUCTION.totalRepayment ?? 0}
									loanTerm={loanCalculationValues.PAYMENT_REDUCTION.numberOfPaymentsMonths}
									ahk={calculatedData?.PAYMENT_REDUCTION.annualPercentageRate ?? 0}
									calculatorPrismicTooltipData={prismicData}
									isExpanded={expandedPaymentPlan === LoanTypeEnum.PAYMENT_REDUCTION}
									onExpand={() => handleExpandPaymentPlan(LoanTypeEnum.PAYMENT_REDUCTION)}
									showOnMobile={isPaymentReductionPlanActiveMobile}
									estimatedPropertyAnnual10YearOption={
										loanCalculationValues.PAYMENT_REDUCTION.estimatedPropertyAnnual10YearOption
									}
									estimatedPropertyAnnualIncrease={
										loanCalculationValues.PAYMENT_REDUCTION.annualHousingPriceIncreasePercentage
									}
									isPaymentReductionPlan
									temporaryReduction={loanCalculationValues.PAYMENT_REDUCTION.optionBAmount}
									durationOfReduction={loanCalculationValues.PAYMENT_REDUCTION.optionBLengthInYears}
									settlementCosts={calculatedData?.PAYMENT_REDUCTION.settlementCosts ?? []}
									loanAmount={loanCalculationValues.PAYMENT_REDUCTION.loanAmount}
									paymentReductionMinAmount={calculatedData?.PAYMENT_REDUCTION.paymentReductionMinAmount}
									paymentReductionMaxAmount={calculatedData?.PAYMENT_REDUCTION.paymentReductionMaxAmount}
									defaultOptions={defaultOptions}
								/>
							</div>

							{/* Payment Plan */}
							<div
								className={styles.bodyPaymentPlan}
								aria-expanded={expandedPaymentPlan !== undefined}
								id='payment-plan'
							>
								<NewCalculatorPaymentPlan
									paymentPlanProps={activeCalculatedData ?? initialLoanCalculationCalculatedData}
									title={
										displayedPaymentPlanType === LoanTypeEnum.ASSET_FORMATION
											? TranslatedStrings.is.newCalculator.paymentPlanTitle
											: TranslatedStrings.is.newCalculator.paymentPlanTitleReduction
									}
									isPaymentReductionPlan={expandedPaymentPlan !== LoanTypeEnum.ASSET_FORMATION}
								/>
							</div>

							{/* Settings Button */}
							<div className={styles.bodyExtraButton} aria-expanded={publicShowSettings}>
								<div className={styles.showMoreButton}>
									<ShowMoreButton
										publicShowSettings={publicShowSettings}
										setPublicShowSettings={changePublicShowSettings}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</GridItem>
		</MainGridContainer>
	)
}

export default NewCalculatorSlice
